"use client";

import { useMemo } from "react";
import { Check, Copy, Globe, Lock, LucideIcon, Share2 } from "lucide-react";
import { LomdaVisibilityType } from "@yedalabs/schema";

import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { defaultLocale, t } from "@/lib/localization";
import { parseLomdaV3Metadata } from "@/lib/utils/lomdaV3/parse";
import { useLocale } from "@/hooks/useLocale";
import { useCopyToClipboard } from "@/hooks/useCopyToClipboard";
import { useLomdaShareLinks } from "@/features/lomdaV3Editor/hooks/useLomdaShareLinks";
import { useLomdaVisibility } from "@/features/lomdaV3Editor/hooks/useLomdaVisibility";
import { ShareHeader } from "@/features/lomdaV3Editor/components/share/shareHeader";
import { GetLomdaV3ResponseType } from "../hooks/useGetLomdaV3";

const VISIBILITY_OPTIONS: {
    value: LomdaVisibilityType;
    keyName: string;
    icon: LucideIcon;
}[] = [
    { value: "public", keyName: "lomda-v3-editor.share.public", icon: Globe },
    { value: "private", keyName: "lomda-v3-editor.share.private", icon: Lock },
];

function CopyableLink({
    value,
    disabledReason,
    label = t("lomda-v3-editor.share.link-label"),
    copyLabel = t("lomda-v3-editor.share.copy"),
    copiedLabel = t("lomda-v3-editor.share.copied"),
    code = false,
}: {
    value: string;
    disabledReason?: string;
    label?: string;
    copyLabel?: string;
    copiedLabel?: string;
    // HTML reads left to right in Hebrew too.
    code?: boolean;
}) {
    const { copied, copy } = useCopyToClipboard();
    const isDisabled = !!disabledReason;

    const handleCopy = () => {
        if (!isDisabled) copy(value);
    };

    const inner = (
        <div
            dir={code ? "ltr" : undefined}
            className={cn("relative", isDisabled && "cursor-not-allowed")}
        >
            <Input
                disabled
                aria-label={label}
                className={cn(
                    "!cursor-default !opacity-100 truncate pe-9 text-muted-foreground",
                    isDisabled && "pointer-events-none !cursor-not-allowed"
                )}
                value={value}
            />
            <button
                type="button"
                onClick={handleCopy}
                aria-disabled={isDisabled}
                aria-label={copied ? copiedLabel : copyLabel}
                className={cn(
                    "absolute inset-y-0 end-0.5 my-0.5 flex items-center justify-center rounded px-1.5 transition-colors",
                    isDisabled
                        ? "cursor-not-allowed text-muted-foreground/70"
                        : copied
                          ? "text-primary"
                          : "text-muted-foreground hover:bg-background hover:text-foreground"
                )}
            >
                {isDisabled ? (
                    <Lock className="size-3.5" />
                ) : copied ? (
                    <Check className="size-3.5" />
                ) : (
                    <Copy className="size-3.5" />
                )}
            </button>
        </div>
    );

    if (!isDisabled) return inner;

    return (
        <TooltipProvider delayDuration={150}>
            <Tooltip>
                <TooltipTrigger asChild>{inner}</TooltipTrigger>
                <TooltipContent
                    side="bottom"
                    className="max-w-[240px] bg-foreground text-center text-background"
                >
                    {disabledReason}
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}

export default function LomdaV3ShareCard({
    lomda,
}: {
    lomda: GetLomdaV3ResponseType["data"];
}) {
    const { dir } = useLocale();
    const visibility = lomda.visibility ?? "private";
    const { setVisibility, isPending } = useLomdaVisibility(
        lomda.id,
        visibility
    );
    const language = useMemo(
        () => lomdaLanguage(lomda.metadata),
        [lomda.metadata]
    );
    const { shareUrl, embedCode } = useLomdaShareLinks(
        lomda.id,
        lomda.name,
        language
    );

    return (
        <div className="overflow-hidden rounded-md border bg-card shadow-sm">
            <ShareHeader
                icon={Share2}
                title={t("lomda-v3-editor.share.title")}
                description={t("lomda-v3-editor.share.description")}
            />

            <div className="flex flex-col gap-6 p-4">
                <div>
                    <Label>
                        {t("lomda-v3-editor.share.visibility-label")}
                    </Label>
                    <Select
                        disabled={isPending}
                        value={visibility}
                        onValueChange={(v) =>
                            setVisibility(v as LomdaVisibilityType)
                        }
                        dir={dir}
                    >
                        <SelectTrigger className="mt-1">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {VISIBILITY_OPTIONS.map((option) => (
                                <SelectItem
                                    key={option.value}
                                    value={option.value}
                                >
                                    <div className="flex flex-row items-center gap-2">
                                        <option.icon className="size-3.5" />
                                        {t(option.keyName)}
                                    </div>
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <div>
                    <Label>
                        {t("lomda-v3-editor.share.link-label")}
                    </Label>
                    <div className="mt-1">
                        <CopyableLink
                            value={shareUrl}
                            disabledReason={
                                visibility !== "public"
                                    ? t("lomda-v3-editor.share.private-warning")
                                    : undefined
                            }
                        />
                    </div>
                </div>

                <div>
                    <Label>{t("lomda-v3-editor.share.embed-label")}</Label>
                    <div className="mt-1">
                        <CopyableLink
                            value={embedCode}
                            code
                            label={t("lomda-v3-editor.share.embed-label")}
                            copyLabel={t("lomda-v3-editor.share.copy-embed")}
                            copiedLabel={t(
                                "lomda-v3-editor.share.embed-copied"
                            )}
                            disabledReason={
                                visibility !== "public"
                                    ? t(
                                          "lomda-v3-editor.share.private-embed-warning"
                                      )
                                    : undefined
                            }
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

const lomdaLanguage = (rawMetadata: string | null) => {
    try {
        return parseLomdaV3Metadata(rawMetadata)?.language ?? defaultLocale;
    } catch {
        return defaultLocale;
    }
};
